/** @file fitIsland: an island scaled into its slot (aspect kept) with its pivot on the slot's pivot pixel. */
import { blitImage } from '@zilo/pose3d-image-io/image/blitImage';
import { createImage } from '@zilo/pose3d-image-io/image/createImage';
import { resampleImage } from '@zilo/pose3d-image-io/image/resampleImage';
import type { Image } from '@zilo/pose3d-image-io/types/image';
import type { PlacedPart, Point } from './sheetTypes';

/** The image mirrored left to right. */
function mirrored(src: Image): Image {
  const out = createImage(src.width, src.height);
  for (let y = 0; y < src.height; y++) {
    for (let x = 0; x < src.width; x++) {
      const s = (y * src.width + x) * 4;
      out.data.set(src.data.subarray(s, s + 4), (y * src.width + src.width - 1 - x) * 4);
    }
  }
  return out;
}

/** Pixels with alpha above 0. */
const opaque = (img: Image): number => {
  let n = 0;
  for (let i = 3; i < img.data.length; i += 4) if ((img.data[i] ?? 0) > 0) n += 1;
  return n;
};

/**
 * The slot image for `island` (as seen on the sheet, `pivot` in its pixels): mirrored first when the part is drawn
 * flipped (the slot holds the unflipped tile), scaled by the largest factor that keeps the aspect and fits the tile
 * (`nearest` for pixel profiles, `linear` for smooth ones), then placed with the pivot on the slot's pivot pixel on a
 * transparent tile. `clipped` when opaque pixels fell outside the tile.
 */
export function fitIsland(island: Image, pivot: Point, part: Pick<PlacedPart, 'size' | 'pivot' | 'flip'>, mode: 'nearest' | 'linear'): { image: Image; clipped: boolean } {
  const src = part.flip ? mirrored(island) : island;
  const px = part.flip ? island.width - pivot[0] : pivot[0];
  const [w, h] = part.size;
  const s = Math.min(w / src.width, h / src.height);
  const sw = Math.max(1, Math.round(src.width * s));
  const sh = Math.max(1, Math.round(src.height * s));
  const scaled = resampleImage(src, sw, sh, mode);
  const out = createImage(w, h);
  const x = Math.round(part.pivot[0] - (px * sw) / src.width);
  const y = Math.round(part.pivot[1] - (pivot[1] * sh) / src.height);
  blitImage(out, scaled, x, y);
  return { image: out, clipped: opaque(out) < opaque(scaled) };
}
